import {Modal} from "flowbite";

let modalIdCounter = 0;

const MODAL_DEFAULTS = {
    size: 'max-w-2xl',
    backdrop: 'static',
    title: 'Modal title'
};

const MODAL_SIZE_CLASSES = [
    'max-w-sm', 'max-w-md', 'max-w-lg',
    'max-w-xl', 'max-w-2xl', 'max-w-3xl'
];

/**
 * Creates an AJAX-powered modal
 * @param {Object} params - Modal parameters
 * @param {string} params.title - Modal title
 * @param {string} params.size - Modal size class
 * @param {string} params.backdrop - Backdrop behavior
 * @param {Object} params.ajax - AJAX configuration
 * @returns {Promise<string>} Modal ID
 */
export function createAjaxModal(params) {
    return new Promise((resolve, reject) => {
        createModal(
            params.title,
            params.size,
            params.backdrop,
            (modalId) => {
                const targetSelector = `#${modalId} .htmx-modal-body`;
                htmx.process(targetSelector);

                const handleAfterSettle = (event) => {
                    if (event.target.closest(targetSelector)) {
                        document.removeEventListener("htmx:afterSettle", handleAfterSettle);
                        resolve(modalId);
                    }
                };

                document.addEventListener("htmx:afterSettle", handleAfterSettle);
                htmx.ajax(
                    params.ajax.verb,
                    params.ajax.path,
                    {
                        ...params.ajax.context,
                        target: targetSelector,
                        swap: "innerHTML"
                    }
                ).catch(reject);
            }
        );
    });
}


/**
 * Creates and displays a modal
 * @param {string} title - Modal title
 * @param {string} size - Modal size class
 * @param {string} backdrop - Backdrop behavior
 * @param {Function} onShow - Callback when modal is shown
 * @returns {string} Modal ID
 */
export function createModal(title, size, backdrop, onShow) {
    const modalId = `htmx-modal-${modalIdCounter++}`;
    const modalElement = createModalElement(modalId, title, size);
    const modal = initializeModal(modalElement, modalId, backdrop, onShow);

    attachModalEventListeners(modalElement, modal);
    modal.show();

    return modalId;
}

/**
 * Creates the modal DOM element
 */
function createModalElement(modalId, title, size) {
    const template = document.getElementById('modal-template');
    const clone = template.content.cloneNode(true);
    const modalDiv = clone.querySelector('div');
    modalDiv.id = modalId;

    document.getElementById('mainContent').appendChild(clone);

    const modalElement = document.getElementById(modalId);
    updateModalTitle(modalElement, title);
    updateModalSize(modalElement, size);

    return modalElement;
}

/**
 * Updates modal title
 */
function updateModalTitle(modalElement, title) {
    const modalTitle = modalElement.querySelector('.htmx-modal-title');
    modalTitle.textContent = title || MODAL_DEFAULTS.title;
}

/**
 * Updates modal size
 */
function updateModalSize(modalElement, size) {
    const modalDialog = modalElement.querySelector('.modal-dialog');
    modalDialog.classList.remove(...MODAL_SIZE_CLASSES);
    modalDialog.classList.add(size || MODAL_DEFAULTS.size);
}

/**
 * Initializes the Modal instance
 */
function initializeModal(modalElement, modalId, backdrop, onShow) {
    let modalInstance = null;

    const options = {
        backdrop: backdrop || MODAL_DEFAULTS.backdrop,
        closable: true,
        onHide: () => {
            modalInstance.destroy();
            modalElement.remove();
        },
        onShow: () => onShow?.(modalId)
    };

    const instanceOptions = {
        id: modalId,
        override: true
    };

    modalInstance = new Modal(modalElement, options, instanceOptions);
    return modalInstance;
}

/**
 * Attaches event listeners to modal
 */
function attachModalEventListeners(modalElement, modal) {
    modalElement.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]')) {
            modal.hide();
        }
    });

    modalElement.addEventListener('close.modal', () => modal.hide());
}

/**
 * Initializes modals for elements with data-modal attribute
 * @param {HTMLElement} parent - Parent element to search within
 */
export function init(parent) {
    parent.querySelectorAll('[data-modal]').forEach((trigger) => {
        if (!trigger.modalLoaded) {
            trigger.modalLoaded = true;
            trigger.addEventListener('click', (event) => {
                if (!trigger.hasAttribute('disabled')) {
                    event.stopPropagation();

                    const modalId = createModal(
                        trigger.dataset.modalTitle,
                        trigger.getAttribute('data-modal-size'),
                        trigger.dataset.modalBackdrop,
                        (id) => {
                            const modal = document.getElementById(id);
                            modal.trigger = trigger;
                            trigger.setAttribute("hx-target", `#${id} .htmx-modal-body`);
                            htmx.process(trigger);
                            trigger.dispatchEvent(new Event("modalCreated"));
                        }
                    );
                }
            });
        }
    });
}

/**
 * Global listener for modal close events
 */
document.addEventListener('modal:close', (event) => {
    // Find the modal from the event target or any open modal
    let modalElement = event.target.closest('[data-modal-backdrop]');

    if (!modalElement) {
        // If not found, look for any visible modal in the DOM
        modalElement = document.querySelector('[data-modal-backdrop]:not(.hidden)');
    }

    if (modalElement) {
        modalElement.dispatchEvent(new Event('close.modal'));
    }
});

/**
 * Global listener for modal refresh events
 */
document.addEventListener('modal:refresh', (event) => {
    const modalElement = event.target.closest('[data-modal-backdrop]');
    if (modalElement) {
        modalElement.trigger.dispatchEvent(new Event("modalCreated"));
    }
});

